import React, { FC, ReactNode, useEffect, useRef } from 'react'
import ReactQuill from 'react-quill'
import he from 'he'
import { makeStyles } from 'tss-react/mui'
import { Box } from '@mui/material'
import 'react-quill/dist/quill.snow.css'

type QuillProps = {
  onChange?: (e: any) => void
  id?: string
  value?: string
  placeholder?: string
  label?: string
  name?: string
  extraModules?: any[]
  extraFormats?: string[]
  className?: string
  customOnChange?: boolean
  children?: ReactNode
  onBlur?: (e: any) => void
  autoFocus?: boolean
  error?: string
}

const useStyles = makeStyles()(theme => ({
  quillStyles: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderWidth: 2,

    '& .quill': {
      paddingBottom: 0,
      paddingTop: 0,
      borderTopRightRadius: '5px',
      borderTopLeftRadius: '5px',
      boxShadow: '2px 2px 2px 0 #888888',
      transition: '0.3s',
    },
    '& .quill:hover': {
      boxShadow: '3px 3px 2px 0 #888888',
    },
    '& .ql-editor': {
      minHeight: '80px',
      backgroundColor: 'rgba(0, 0, 0, 0.06)',
    },
    '& .ql-toolbar': {
      borderTopLeftRadius: '5px',
      borderTopRightRadius: '5px',
    },
    '.quill.ql-error': {
      '.ql-toolbar': {
        borderBottom: '1px solid ' + theme.palette.error.main,
      },
      '.ql-container': {
        borderLeft: '1px solid ' + theme.palette.error.main,
        borderRight: '1px solid ' + theme.palette.error.main,
        borderBottom: '1px solid ' + theme.palette.error.main,
      },
    },
  },
  errorText: {
    color: theme.palette.error.main,
  },
}))
const Quill: FC<QuillProps> = props => {
  const {
    onChange,
    value = '',
    extraModules = {},
    extraFormats = [],
    children,
    name = '',
    onBlur,
    customOnChange,
    autoFocus = false,
    error,
    ...leftoverProps
  } = props

  const { classes } = useStyles()
  const quillRef = useRef<ReactQuill>()

  useEffect(() => {
    autoFocus && quillRef?.current.editor.focus()
  }, [])

  const modules = {
    toolbar: [
      [{ header: '1' }, { header: '2' }, { font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
      [{ color: [] }, { background: [] }],
      ['clean'],
    ],
    clipboard: { matchVisual: true },
    ...extraModules,
  }
  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'color',
    'background',
    ...extraFormats,
  ]

  const onChangeObject = (e: string) => {
    return customOnChange
      ? he.encode(e)
      : {
          target: {
            validity: {
              valid: true,
            },
            name,
            value: he.encode(e),
          },
        }
  }
  return (
    <div className={classes.quillStyles}>
      <ReactQuill
        theme="snow"
        value={he.decode(value || '')}
        onChange={e => onChange(onChangeObject(e))}
        modules={modules}
        onBlur={onBlur}
        formats={formats}
        ref={quillRef}
        className={error ? 'ql-error' : ''}
        {...leftoverProps}
      />
      {error && (
        <Box paddingBottom={1}>
          <span className={classes.errorText}>{error}</span>
        </Box>
      )}
    </div>
  )
}

export const displayText = (value: string | null) => {
  return {
    dangerouslySetInnerHTML: {
      __html: he.decode(value || ''),
    },
  }
}

export const stripHTML = (e: string | null) => he.unescape(e || '').replace(/<\/?[^>]+(>|$)/gi, '')
export const decode = v => he.decode(v || '')
export const encode = v => he.encode(v || '')
export default Quill
